import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CreateDesignDto } from "../../core/models/dtos/CreateDesignDto";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import { getActAsOrgId } from "../../contexts/actAsStore";
import { PageQuery, toQueryParam } from "../../utils/models/pageQuery";
import { Design } from "../../core/models/entities/Design";
import { PagedResponse } from "../models/responses/pagedResponse";
import { mapToDesign } from "../models/mappers/mapDesign";
import { InitiateApproval } from "../../presentation/screens/logo-list/hooks/useLogoActionsHandler";
import { Verdict } from "../../presentation/components/ApprovalVoting/approval-voting";
import { ChangeStyleBody, ChangeTitleBody } from "../models/bodies/ChangeBody";
import { CreateMaskQueueCount } from "../models/responses/CreateMaskQueueCount";

import { DesignDto, DesignNoteDto } from "../models/responses/DesignDto";
import { enviroment } from "../../enviroment";
import { withClick } from "../clients/clientCorrelation";
import { withProductPendingRetry } from "../clients/productPendingRetry";
import {
  patchDesignInQueryCache,
  removeDesignFromQueryCache,
  savedDesignLogoSetMatchesCache,
} from "./designCachePatch";
import { useEffect } from "react";

export interface DesignRepository {
  useAllDesigns: (
    query: PageQuery,
    options?: { keepPreviousData?: boolean },
  ) => Resource<PagedResponse<Design>>;
  prefetchDesigns: (query: PageQuery) => Promise<void>;
  useDesign: (organizationId: number, designId: number) => Resource<Design>;
  createDesign: (dto: CreateDesignDto) => Promise<Design>;
  updateDesign: (design: Design) => Promise<Design>;
  commit(design: Design): Promise<Design>;
  initiateApproval(design: Design, approval: InitiateApproval): Promise<Design>;
  placeVerdict(design: Design, verdict: Verdict): Promise<Design>;
  deleteDesign(orgId: number, designId: number): Promise<void>;
  skipApproval(design: Design): Promise<Design>;
  sendApproval(design: Design, email: string): Promise<void>;
  sendApprovalConditional(design: Design, email: string): Promise<void>;
  changeStyle(
    orgId: number,
    designId: number,
    styleId: number
  ): Promise<Design>;
  changeTitle(orgId: number, designId: number, title: string): Promise<Design>;
  updateDesignNote(
    orgId: number,
    designId: number,
    note: string | null
  ): Promise<DesignNoteDto>;
  copyDesign(orgId: number, designId: number): Promise<Design>;
  getImportDesignsQueueCounts(): Promise<CreateMaskQueueCount[]>;
  useImportDesignsQueueCounts(options?: { open?: boolean }): Resource<CreateMaskQueueCount[]>;
  getDesignPrices(designIds: number[]): Promise<Design[]>;
  updatePricesForDesigns(query: PageQuery, designIds: number[]): Promise<void>;
}

export function useDesignRepository(): DesignRepository {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  // Act-as-vendor: getAllDesigns is scoped by ApiClient (actAsOrgId param), so
  // the impersonation scope must be part of the list cache keys — otherwise
  // the admin's global rows are served from cache after switching identity.
  // 0 = not impersonating. See OrderRepository for the full rationale.
  const actAsKey = () => getActAsOrgId() ?? 0;

  async function invalidateQueries() {
    await queryClient.invalidateQueries({
      queryKey: ["designs"],
      type: "all",
    });
  }

  function useAllDesigns(
    query: PageQuery,
    options?: { keepPreviousData?: boolean },
  ): Resource<PagedResponse<Design>> {
    return useQuery(
      ["designs", actAsKey(), toQueryParam(query)],
      ({ signal }) => apiClient.getAllDesigns(query, false, signal),
      {
        // Null-guard on results: a throw inside select puts the query in a
        // 200-backed error state that never heals (same failure mode as the
        // mapMultiProductRef clone bug).
        select: (data) => ({
          ...data,
          results: data.results ? data.results.map(mapToDesign) : [],
        }),
        // OPT-IN (default off): the design list keeps the previous page's
        // rows visible (dimmed via isFetching/isPreviousData in
        // FunctionalList) while a new search/page loads, instead of
        // collapsing into a spinner. Other consumers (admin design import)
        // rely on isLoading gating and keep the previous behavior.
        keepPreviousData: options?.keepPreviousData ?? false,
        staleTime: 5 * 60 * 1000, // 5 minutes
        // Keep the list cached past React Query's 5 min GC default so a
        // Logo->Design hop after a pause still lands on cache. MUST be paired
        // with refetchOnMount: true (overriding the global false): a stale
        // cached page then renders instantly and revalidates in the
        // background, so freshness is never worse than today.
        cacheTime: 30 * 60 * 1000,
        refetchOnMount: true,
      }
    );
  }

  // Warm a page's cache in the background (same key, call, staleTime and
  // cacheTime as useAllDesigns, so a later click is served instantly from
  // cache). Returns the never-rejecting prefetch promise for in-flight tracking.
  function prefetchDesigns(query: PageQuery): Promise<void> {
    return queryClient.prefetchQuery(
      ["designs", actAsKey(), toQueryParam(query)],
      () => apiClient.getAllDesigns(query, false),
      { staleTime: 5 * 60 * 1000, cacheTime: 30 * 60 * 1000 }
    );
  }

  function useDesign(
    organizationId: number,
    designId: number
  ): Resource<Design> {
    // actAsKey in the DETAIL key too: getDesign now carries actAsOrgId, so the
    // payload varies with the impersonation scope (per-org number-logo storage
    // counts). Same slot position as the list keys; useCartSync's design
    // queries share this key shape and must stay in sync.
    return useQuery(
      ["designs", actAsKey(), designId],
      () => apiClient.getDesign(organizationId, designId),
      {
        select: (data) => mapToDesign(data),
      }
    );
  }

  function createDesign(dto: CreateDesignDto): Promise<Design> {
    return apiClient.createDesign(dto).then((designResponse) => {
      invalidateQueries().then();
      return mapToDesign(designResponse);
    });
  }

  // Save (flag designSavePatchEnabled): the PUT response IS the design after
  // the save, so when its placed logos are the ones the detail cache holds
  // (same rows, no logo edited since) it lands in the caches like a verdict
  // does (see designCachePatch, save patch) instead of refetching GetDesigns
  // (+ GetDesign + cart). A save that added/removed a logo, one whose logo was
  // recoloured/resized in the meantime, or one without a cached detail entry,
  // keeps today's invalidate (its prices need the server's AdjustPrice). The server HAS
  // saved when this runs, so a failure inside the patch must never turn the
  // successful PUT into "update failed": it falls back to today's invalidate.
  function settleSaveResponse(designResponse: DesignDto): Design {
    if (enviroment.designSavePatchEnabled) {
      try {
        if (savedDesignLogoSetMatchesCache(queryClient, designResponse)) {
          patchDesignInQueryCache(queryClient, designResponse);
          return mapToDesign(designResponse);
        }
      } catch (error) {
        console.warn(
          "[designSavePatch] cache patch failed - falling back to invalidate",
          error
        );
      }
    }
    invalidateQueries().then();
    return mapToDesign(designResponse);
  }

  function updateDesign(design: Design): Promise<Design> {
    return apiClient
      .updateDesign(design.customerId, design.id, design)
      .then(settleSaveResponse);
  }

  // 409 productPending (flag productPendingEnabled) on the four commit
  // paths below: info toast + ONE retry after 3 s (withProductPendingRetry);
  // flag off = the plain call. They are the design endpoints behind the
  // API's ProductPendingGuard.
  function commit(design: Design): Promise<Design> {
    return withProductPendingRetry(() => apiClient.commitDesign(design)).then(
      (designResponse) => {
        invalidateQueries().then();
        return mapToDesign(designResponse);
      }
    );
  }

  // Approval mutations (flag approvalPatchEnabled): the response IS the
  // design after the operation, so land it in the caches instead of
  // refetching GetDesigns (+ GetDesign + one GetDesign per cart design) -
  // see designCachePatch for what is taken from the response and what the
  // cache keeps (prices per container rule, storage). Flag off = today's
  // invalidate. The server HAS saved the verdict when this runs, so a
  // failure inside the patch (an unexpected cache shape, a mapper throw)
  // must never turn the successful PUT into "approval failed": it falls
  // back to today's invalidate and the response is returned as usual.
  function settleApprovalResponse(designResponse: DesignDto): Design {
    if (enviroment.approvalPatchEnabled) {
      try {
        patchDesignInQueryCache(queryClient, designResponse);
      } catch (error) {
        console.warn(
          "[approvalPatch] cache patch failed - falling back to invalidate",
          error
        );
        invalidateQueries().then();
      }
    } else {
      invalidateQueries().then();
    }
    return mapToDesign(designResponse);
  }

  function initiateApproval(
    design: Design,
    approval: InitiateApproval
  ): Promise<Design> {
    return withClick(() =>
      withProductPendingRetry(() =>
        apiClient.initiateApprovalDesign(design, approval)
      ).then(settleApprovalResponse)
    );
  }

  function placeVerdict(design: Design, verdict: Verdict): Promise<Design> {
    return withClick(() =>
      withProductPendingRetry(() =>
        apiClient.placeVerdictDesign(design, verdict)
      ).then(settleApprovalResponse)
    );
  }

  function deleteDesign(orgId: number, designId: number): Promise<void> {
    return withClick(() =>
      apiClient.deleteDesign(orgId, designId).then(async () => {
        if (enviroment.approvalPatchEnabled) {
          // Row out of every list page (totalCount decremented), inactive
          // detail entries dropped, active ones refetched (today's 404),
          // lists marked stale without a refetch. Same guard as above: the
          // DELETE has succeeded, so a patch failure falls back to today's
          // invalidate instead of failing the delete.
          try {
            removeDesignFromQueryCache(queryClient, designId);
            return;
          } catch (error) {
            console.warn(
              "[approvalPatch] cache removal failed - falling back to invalidate",
              error
            );
          }
        }
        await invalidateQueries();
      })
    );
  }

  // NOTE: POST .../approval-skip does not exist in the API (only
  // approval-start with skip=true does, which handleApprovalSkip uses); this
  // method has no callers and is kept only for the interface.
  function skipApproval(design: Design): Promise<Design> {
    return withClick(() =>
      withProductPendingRetry(() => apiClient.skipApprovalDesign(design)).then(
        settleApprovalResponse
      )
    );
  }

  async function sendApproval(design: Design, email: string): Promise<void> {
    const lr = await apiClient.sendApprovalDesign(design, email);
  }

  async function sendApprovalConditional(
    design: Design,
    email: string
  ): Promise<void> {
    const lr = await apiClient.sendApprovalDesignConditional(design, email);
  }

  async function changeStyle(
    orgId: number,
    designId: number,
    styleId: number
  ): Promise<Design> {
    const body: ChangeStyleBody = {
      styleId: styleId,
    };

    const dto = await apiClient.changeStyle(orgId, designId, body);
    // The PUT response IS the updated design (same DesignDto the GET endpoint
    // returns). Seed the detail cache with the raw DTO — useDesign stores raw
    // DTOs and maps via `select` — so the editor mounts without its own GET.
    queryClient.setQueryData(["designs", actAsKey(), designId], dto);
    // Non-blocking list invalidation (same pattern as createDesign /
    // updateDesign): the design list refreshes in the background instead of
    // holding the style-change flow hostage.
    invalidateQueries().then();
    return mapToDesign(dto);
  }

  async function changeTitle(
    orgId: number,
    designId: number,
    title: string
  ): Promise<Design> {
    const body: ChangeTitleBody = {
      newName: title,
    };

    const result = apiClient
      .changeDesignTitle(orgId, designId, body)
      .then(async (res) => {
        await invalidateQueries();
        return res;
      });
    return result;
  }

  // Customer-editable design note. Optimistic-ish cache update: the PUT response
  // carries the saved note + audit stamp, so on success we patch the DETAIL cache
  // in place (raw DTO — useDesign maps via `select`) instead of refetching the
  // whole design, and refresh the list caches in the background (non-blocking,
  // same pattern as changeStyle) so list payloads pick the note up eventually.
  async function updateDesignNote(
    orgId: number,
    designId: number,
    note: string | null
  ): Promise<DesignNoteDto> {
    const res = await apiClient.updateDesignNote(orgId, designId, note);
    queryClient.setQueryData(
      ["designs", actAsKey(), designId],
      (old: DesignDto | undefined) =>
        old
          ? {
              ...old,
              customerNote: res.customerNote,
              customerNoteUpdatedAtUtc: res.customerNoteUpdatedAtUtc,
              customerNoteUpdatedByName: res.customerNoteUpdatedByName,
            }
          : old
    );
    invalidateQueries().then();
    return res;
  }

  async function copyDesign(orgId: number, designId: number): Promise<Design> {
    const dto = await apiClient.copyDesign(orgId, designId);
    // The copy response IS the new design (same DesignDto shape as GET). Seed
    // the detail cache under the NEW id so the editor mounts without its own
    // GET, and refresh the design list in the background instead of blocking
    // navigation on a refetch of the whole list.
    queryClient.setQueryData(["designs", actAsKey(), dto.id], dto);
    // The copy response carries no prices (the backend only runs AdjustPrice on
    // GET), and the global refetchOnMount:false means a seeded-but-inactive key
    // would otherwise never heal — the details modal would show "no available
    // prices" from the stale seed. Force a background refetch of the seeded key
    // (refetchType "all" covers inactive queries) while the editor mounts
    // instantly from the seed.
    queryClient
      .invalidateQueries({
        queryKey: ["designs", actAsKey(), dto.id],
        refetchType: "all",
      })
      .then();
    invalidateQueries().then();
    return mapToDesign(dto);
  }

  function getImportDesignsQueueCounts(): Promise<CreateMaskQueueCount[]> {
    return apiClient.getImportDesignsQueueCounts();
  }

  // React Query version for the header queue badge: dedups the header's repeated
  // remounts into one request (the imperative call used to fire ~5x per page load)
  // while keeping the 60s polling via refetchInterval.
  // Admin perf FE-6 (flag adminPerfQueueLazyEnabled): the HEADER instance
  // passes `open` (pointer over the queue box). With the flag it polls every
  // 5 min while not looked at, 60 s while it is, and refetches at once when
  // looked at after >55 s - so the number is never older than 60 s when the
  // admin looks at it, at ~80 % fewer apiPeter calls per tab. Callers that
  // pass no `open` (the import screens' own 60 s polls) are untouched, as
  // is everything with the flag off.
  function useImportDesignsQueueCounts(options?: {
    open?: boolean;
  }): Resource<CreateMaskQueueCount[]> {
    const lazy =
      enviroment.adminPerfQueueLazyEnabled && options?.open !== undefined;
    const query = useQuery(
      ["import-queue-counts", "designs"],
      () => apiClient.getImportDesignsQueueCounts(),
      lazy
        ? {
            refetchInterval: options?.open ? 60000 : 300000,
            staleTime: 55000,
            refetchOnMount: true,
          }
        : {
            refetchInterval: 60000,
            staleTime: 55000,
          },
    );
    const { isStale, refetch, isFetching } = query;
    useEffect(() => {
      if (lazy && options?.open && isStale && !isFetching) refetch();
      // Only the open/close edge matters (isStale/isFetching read at that moment).
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lazy, options?.open]);
    return query;
  }

  function getDesignPrices(designIds: number[]): Promise<Design[]> {
    return apiClient
      .getDesignPrices(designIds)
      .then((dtos: DesignDto[]) => (dtos ?? []).map(mapToDesign));
  }

  async function updatePricesForDesigns(
    query: PageQuery,
    designIds: number[]
  ): Promise<void> {
    if (designIds.length === 0) return;
    const pricedDtos = (await apiClient.getDesignPrices(designIds)) ?? [];

    // Same key shape as useAllDesigns/prefetchDesigns — this write targeted
    // ["designs", qs] and has been a no-op since actAsKey() entered the list
    // key (#601). Currently uncalled (the list's price column was removed),
    // but kept correct for a future resurrection.
    queryClient.setQueryData(
      ["designs", actAsKey(), toQueryParam(query)],
      (oldData: PagedResponse<DesignDto> | undefined) => {
        if (!oldData?.results) return oldData;

        const newResults = oldData.results.map((dto) => {
          const priced = pricedDtos.find((p) => p.id === dto.id);
          if (priced) {
            return {
              ...dto,
              product: priced.product,
              heating: priced.heating,
              priceSort: priced.priceSort,
              designLogos: priced.designLogos,
            };
          }
          return dto;
        });

        return {
          ...oldData,
          results: newResults,
        };
      }
    );
  }

  /*
  *
  delete(orgId: number, designId: number): Promise<void>
  * */

  return {
    useAllDesigns,
    prefetchDesigns,
    useDesign,
    createDesign,
    updateDesign,
    commit,
    deleteDesign,
    placeVerdict,
    initiateApproval,
    skipApproval,
    sendApproval,
    sendApprovalConditional,
    changeStyle,
    changeTitle,
    updateDesignNote,
    copyDesign,
    getImportDesignsQueueCounts,
    useImportDesignsQueueCounts,
    getDesignPrices,
    updatePricesForDesigns,
  };
}
